// src/cockpit4/components/ProcessAutoOptimizer.tsx
// ============================================
// 流程自动优化（工作流中心 P2-功能8）- v2.0 详细改进计划
// AI 发现流程优化点 / 自动生成优化方案 / A/B 测试验证 / 持续迭代 / 效果追踪
// 数据来源：localStorage（xeaos_process_optimizer）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Workflow, Zap, FlaskConical, TrendingUp, CheckCircle2 } from "lucide-react";

interface OptimizeSuggestion {
  id: string;
  workflow: string;
  bottleneck: string;
  current: string;
  proposed: string;
  saving: string;
  impact: number;
  status: "建议" | "验证中" | "已实施" | "已驳回";
}

const INITIAL: OptimizeSuggestion[] = [
  { id: "o1", workflow: "内容生成流程", bottleneck: "图片生成串行等待", current: "串行生成 5 张图，耗时 45s", proposed: "并行生成 + 预选模板", saving: "单次节省 30s", impact: 82, status: "验证中" },
  { id: "o2", workflow: "CRM 线索分配", bottleneck: "人工分配延迟", current: "人工判断分配平均 4h", proposed: "规则引擎自动分配", saving: "响应提升 90%", impact: 91, status: "建议" },
  { id: "o3", workflow: "日报生成", bottleneck: "数据汇总重复", current: "每次手动拉取 6 个模块", proposed: "自动聚合 + 模板渲染", saving: "每日节省 20min", impact: 76, status: "已实施" },
  { id: "o4", workflow: "合同审批", bottleneck: "多级串行审批", current: "3 级串行平均 3 天", proposed: "并行审批 + 自动催办", saving: "缩短至 1.5 天", impact: 68, status: "已驳回" },
];

const KEY = "xeaos_process_optimizer";

function load(): OptimizeSuggestion[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as OptimizeSuggestion[];
  } catch { /* ignore */ }
  return INITIAL;
}

export default function ProcessAutoOptimizer() {
  const [items, setItems] = useState<OptimizeSuggestion[]>(load);

  const stats = useMemo(() => {
    const implemented = items.filter((i) => i.status === "已实施").length;
    const avgImpact = items.reduce((a, i) => a + i.impact, 0) / items.length;
    const active = items.filter((i) => i.status === "建议" || i.status === "验证中").length;
    return { implemented, avgImpact, active };
  }, [items]);

  const setStatus = (id: string, status: OptimizeSuggestion["status"]) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Workflow size={14} className="text-blue-400" />
        <h3 className="text-[12px] font-medium text-white">流程自动优化</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能8 · Auto Optimize</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="已实施优化" value={`${stats.implemented} 项`} color="#34D399" />
        <MetricCard label="平均影响度" value={`${stats.avgImpact.toFixed(0)}%`} color="#38BDF8" />
        <MetricCard label="待验证" value={`${stats.active} 项`} color="#FBBF24" />
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <Zap size={11} className="text-amber-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">AI 发现的优化点</span>
        </div>
        <div className="space-y-1.5">
          {items.map((i) => (
            <div key={i.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-white">{i.workflow}</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-red-500/10 text-red-300">瓶颈：{i.bottleneck}</span>
                <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded ${
                  i.status === "已实施" ? "bg-emerald-500/15 text-emerald-400" : i.status === "验证中" ? "bg-sky-500/15 text-sky-400" : i.status === "建议" ? "bg-amber-500/15 text-amber-400" : "bg-gray-500/15 text-gray-500"
                }`}>{i.status}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-1.5 text-[9px] font-mono">
                <div className="rounded bg-white/[0.03] p-1.5"><span className="text-gray-500">现状：</span><span className="text-gray-400">{i.current}</span></div>
                <div className="rounded bg-blue-500/[0.06] p-1.5"><span className="text-blue-400">方案：</span><span className="text-gray-400">{i.proposed}</span></div>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[9px] font-mono text-emerald-400">预计 {i.saving}</span>
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden mx-1">
                  <div className="h-full rounded-full" style={{ width: `${i.impact}%`, background: i.impact >= 80 ? "#34D399" : "#38BDF8" }} />
                </div>
                <span className="text-[9px] font-mono text-gray-500">影响 {i.impact}%</span>
                {i.status === "建议" && (
                  <div className="flex gap-1">
                    <button onClick={() => setStatus(i.id, "验证中")} className="text-[8px] font-mono px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300"><FlaskConical size={8} className="inline" /> 开始验证</button>
                    <button onClick={() => setStatus(i.id, "已驳回")} className="text-[8px] font-mono px-1.5 py-0.5 rounded border border-white/[0.06] bg-white/[0.02] text-gray-500">驳回</button>
                  </div>
                )}
                {i.status === "验证中" && (
                  <button onClick={() => setStatus(i.id, "已实施")} className="text-[8px] font-mono px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"><CheckCircle2 size={8} className="inline" /> 确认实施</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp size={11} className="text-emerald-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">优化效果追踪</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[9px] font-mono">
          {[
            { color: "#34D399", text: "已实施 3 项优化，平均流程耗时下降 32%" },
            { color: "#38BDF8", text: "A/B 验证 1 项进行中，预计本周出结论" },
            { color: "#FBBF24", text: "优化建议自动推送至审批流，无需人工整理" },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
              <span className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0" style={{ background: s.color }} />
              <span className="text-gray-400 leading-relaxed">{s.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

